import cn from "clsx";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { toast } from "react-toastify";

import { ProductSummary } from "@/common/components/product-summary";
import { formatDateCompact } from "@/common/hooks/use-date";
import {
  ConfigSummaryItem,
  useProductConfigSummary,
} from "@/features/product/components/hooks/use-product-config-summary";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { formatPrice } from "@/features/product/hooks/use-price";
import { productTestIds } from "@/features/product/test-ids";

export function ProductOrderSummary({ title }: { title: string }) {
  const t = useTranslations("common");
  const { config, attributes, deliveryOption, artworkCheckPrice } = useStoreProduct();

  const copyTextToClipboard = (text: ConfigSummaryItem[]) => {
    const copyingText = text.map((item) => item.key + ": " + item.value).join(" | ");
    navigator.clipboard.writeText(copyingText);
    toast.success("Copied to clipboard");
  };

  // match config items to product options value arrays
  const { configSummary, spine } = useProductConfigSummary({ attributes, config });

  return (
    <div id="orderSummary" className="bg-success-light relative -mx-4 mt-6 p-4 lg:mx-0 lg:mt-10">
      <p
        data-testid={productTestIds.orderSummaryHeadline}
        className="text-grey-90 text-center text-xl font-semibold md:text-2xl"
      >
        {t("text-product-order-summary")}
        <br />
        {title}
      </p>
      <Image
        src="/assets/images/CopyIcon.webp"
        className="absolute right-5 top-5 cursor-pointer"
        alt="test"
        width={20}
        height={20}
        onClick={() => copyTextToClipboard(configSummary)}
      />
      <div className={cn("pt-6 text-center", { "mb-2": spine })}>
        <ProductSummary
          configSummary={configSummary}
          spine={spine}
          className="justify-center text-base"
          spineClassName="mb-2 text-base"
        />
        {deliveryOption && (
          <p className="text-grey-90 mb-2 text-base">
            <span className="font-bold">{t("text-production")}: </span>
            {deliveryOption.name}
            {". "}
            {t("text-production-days-for-delivery", {
              days: deliveryOption.productionDays,
            })}{" "}
            {formatDateCompact(deliveryOption.deliveryDate)}
            <sup className="top-[-0.25em]">*</sup>
          </p>
        )}
        <p className="text-grey-90 mb-2 text-base">
          <span className="font-bold">{t("text-artwork-check")}: </span>
          {artworkCheckPrice
            ? `${t("text-expert")} - ${formatPrice({
                centAmount: artworkCheckPrice,
                currencyCode: "GBP",
                fractionDigits: 2,
              })}`
            : `${t("text-essential")} - ${t("text-free-uppercase")}`}
        </p>
        {deliveryOption && (
          <>
            <p className="text-grey-90 mb-6 text-sm italic">
              <sup className="top-[-0.25em]">*</sup> {t("text-artwork-dependant")}{" "}
              {t("text-considering-holidays")}
            </p>
            <p className="text-grey-90 mb-2 text-base font-bold">
              {t("text-free-uk-mainland-delivery")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
